// File: src/pages/BootDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "../css/Lp2App.css";
import styles from "./BootDetail.module.css";
import { apiUrl } from "../config/api";
import {
  BootCondition,
  ShoeType,
  conditionLabel,
  shoeTypeLabel,
} from "../api/bootloopClient";
import { resolvePhotoUrl } from "../utils/resolvePhotoUrl";

type ListingPhotoDto = {
  PhotoId?: number;
  photoId?: number;

  ListingId?: number;
  listingId?: number;

  Url?: string;
  url?: string;

  UrlThumb?: string | null;
  urlThumb?: string | null;

  UrlMedium?: string | null;
  urlMedium?: string | null;

  SortOrder?: number;
  sortOrder?: number;
};

type BootListingDetailDto = {
  ListingId?: number;
  listingId?: number;

  SizeUk?: number;
  sizeUk?: number;

  ShoeType?: number;
  shoeType?: number;

  Condition?: number;
  condition?: number;

  Notes?: string | null;
  notes?: string | null;

  Status?: number;
  status?: number;

  CreatedUtc?: string;
  createdUtc?: string;

  OwnerArea?: string | null;
  ownerArea?: string | null;

  PhotoUrl?: string | null;
  photoUrl?: string | null;

  Photos?: ListingPhotoDto[] | null;
  photos?: ListingPhotoDto[] | null;

  DistanceMiles?: number | null;
  distanceMiles?: number | null;
};

type SetStatusRequest = {
  status: 1 | 3;
  ownerEmail: string;
};

function pick<T>(obj: any, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k] as T;
  }
  return undefined;
}

function formatDistance(miles?: number | null): string | null {
  if (miles == null) return null;
  if (miles < 0.1) return "< 0.1 miles";
  if (miles < 10) return `${miles.toFixed(1)} miles`;
  return `${Math.round(miles)} miles`;
}

type Tone = "close" | "mid" | "far";
function getTone(miles?: number | null): Tone {
  if (miles == null) return "mid";
  if (miles < 5) return "close";
  if (miles < 20) return "mid";
  return "far";
}

const BootDetail: React.FC = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { listingId } = useParams();
  const id = Number(listingId || 0) || 0;

  // ✅ Public visitor mode
  const qs = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const isPublic = qs.get("public") === "1";
  const backPath = qs.get("back") || "/boots";

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");

  const [dto, setDto] = useState<BootListingDetailDto | null>(null);
  const [selected, setSelected] = useState(0);

  const ownerEmail = (localStorage.getItem("bootloop.email") || "").trim();
  const viewerPostcode = (localStorage.getItem("bootloop.postcode") || "").trim();

  const [savingStatus, setSavingStatus] = useState(false);

  const normalized = useMemo(() => {
    if (!dto) return null;

    const ListingId = pick<number>(dto, "ListingId", "listingId") ?? 0;
    const SizeUk = pick<number>(dto, "SizeUk", "sizeUk") ?? 0;
    const ShoeType = pick<number>(dto, "ShoeType", "shoeType") ?? 0;
    const Condition = pick<number>(dto, "Condition", "condition") ?? 0;
    const Notes = pick<string | null>(dto, "Notes", "notes") ?? null;
    const Status = pick<number>(dto, "Status", "status") ?? 0;
    const OwnerArea = pick<string | null>(dto, "OwnerArea", "ownerArea") ?? null;
    const PhotoUrl = pick<string | null>(dto, "PhotoUrl", "photoUrl") ?? null;
    const DistanceMiles =
      pick<number | null>(dto, "DistanceMiles", "distanceMiles") ?? null;

    const Photos =
      (pick<ListingPhotoDto[] | null>(dto, "Photos", "photos") ?? []) || [];

    const sortedPhotos = [...Photos].sort((a, b) => {
      const ao = pick<number>(a, "SortOrder", "sortOrder") ?? 0;
      const bo = pick<number>(b, "SortOrder", "sortOrder") ?? 0;
      if (ao !== bo) return ao - bo;
      const aid = pick<number>(a, "PhotoId", "photoId") ?? 0;
      const bid = pick<number>(b, "PhotoId", "photoId") ?? 0;
      return aid - bid;
    });

    return {
      ListingId,
      SizeUk,
      ShoeType,
      Condition,
      Notes,
      Status,
      OwnerArea,
      PhotoUrl,
      Photos: sortedPhotos,
      DistanceMiles,
    };
  }, [dto]);

  const images = useMemo(() => {
    if (!normalized) return [];

    const list: Array<{ full: string; thumb: string }> = [];

    if (normalized.Photos.length > 0) {
      for (const p of normalized.Photos) {
        const url = pick<string>(p, "Url", "url") || "";
        const urlThumb = pick<string | null>(p, "UrlThumb", "urlThumb") || null;
        const urlMedium =
          pick<string | null>(p, "UrlMedium", "urlMedium") || null;

        const full = resolvePhotoUrl(urlMedium || url);
        const thumb = resolvePhotoUrl(urlThumb || urlMedium || url);

        if (full) list.push({ full, thumb: thumb || full });
      }
    }

    if (list.length === 0) {
      const legacy = resolvePhotoUrl(normalized.PhotoUrl || "");
      if (legacy) list.push({ full: legacy, thumb: legacy });
    }

    return list;
  }, [normalized]);

  const load = async () => {
    if (!id) return;

    setLoading(true);
    setErr("");

    try {
      const qs = viewerPostcode
        ? `?viewerPostcode=${encodeURIComponent(viewerPostcode)}`
        : "";

      const res = await fetch(apiUrl(`/api/listings/${id}${qs}`));
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to load (${res.status}): ${text}`);
      }

      const json = (await res.json()) as BootListingDetailDto;
      setDto(json);
      setSelected(0);
    } catch (e: any) {
      setErr(e?.message || "Failed to load");
      setDto(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const markSwapped = async () => {
    if (!id) return;

    const em = ownerEmail;
    if (!em) {
      setErr("Enter your email to mark swapped.");
      return;
    }

    if (!window.confirm("Mark this listing as swapped?")) return;

    setSavingStatus(true);
    setErr("");

    const payload: SetStatusRequest = { status: 3, ownerEmail: em };

    try {
      const res = await fetch(apiUrl(`/api/listings/${id}/status`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed (${res.status}): ${text}`);
      }

      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  if (!id) {
    return (
      <div className="lp2__page">
        <main className="lp2__container">
          <div className={styles.card}>
            <div>Invalid listing id.</div>
            <div className={styles.actions}>
              <button className={styles.ghostBtn} onClick={() => nav("/boots")}>
                Back
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const status = normalized?.Status ?? 0;

  const miles =
    normalized?.DistanceMiles !== null && normalized?.DistanceMiles !== undefined
      ? normalized.DistanceMiles
      : null;

  const distanceText = formatDistance(miles);
  const tone = getTone(miles);
  const distanceClass =
    tone === "close"
      ? styles.distanceClose
      : tone === "far"
      ? styles.distanceFar
      : styles.distanceMid;

  return (
    <div className="lp2__page">
      <main className="lp2__container">
        <div className={styles.card}>
          <div className={styles.headerRow}>
            <h1 className={styles.h1}>Listing detail</h1>

            <div className={styles.headerActions}>
              {/* ✅ Visitor/public mode: only show back-to-listings */}
              {isPublic ? (
                <button
                  type="button"
                  className={styles.ghostBtn}
                  onClick={() => nav(backPath)}
                >
                  ← Back to listings
                </button>
              ) : (
                <>
                  <Link className={styles.ghostBtn} to="/my">
                    Back to My stuff
                  </Link>
                  <Link className={styles.ghostBtn} to="/boots">
                    Boots available
                  </Link>
                </>
              )}
            </div>
          </div>

          {err && <div className={styles.error}>{err}</div>}
          {loading && <div className={styles.smallMuted}>Loading…</div>}

          {normalized && (
            <div className={styles.grid}>
              <div className={styles.carousel}>
                <div className={styles.main}>
                  {images.length > 0 ? (
                    <img
                      src={images[Math.min(selected, images.length - 1)].full}
                      alt={`Listing ${normalized.ListingId}`}
                    />
                  ) : (
                    <div className={styles.placeholder}>No photos</div>
                  )}
                </div>

                {images.length > 1 && (
                  <div className={styles.thumbs}>
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`${styles.thumbBtn} ${
                          idx === selected ? styles.thumbActive : ""
                        }`}
                        onClick={() => setSelected(idx)}
                        aria-label={`Photo ${idx + 1}`}
                      >
                        <img src={img.thumb} alt={`thumb ${idx + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className={styles.kpiRow}>
                  <div className={styles.kpi}>
                    <div className={styles.kpiLabel}>Listing</div>
                    <div className={styles.kpiValue}>#{normalized.ListingId}</div>
                  </div>

                  <div className={styles.kpi}>
                    <div className={styles.kpiLabel}>Size</div>
                    <div className={styles.kpiValue}>UK {normalized.SizeUk}</div>
                  </div>

                  <div className={styles.kpi}>
                    <div className={styles.kpiLabel}>Type</div>
                    <div className={styles.kpiValue}>
                      {shoeTypeLabel(normalized.ShoeType as ShoeType)}
                    </div>
                  </div>

                  <div className={styles.kpi}>
                    <div className={styles.kpiLabel}>Condition</div>
                    <div className={styles.kpiValue}>
                      {conditionLabel(normalized.Condition as BootCondition)}
                    </div>
                  </div>
                </div>

                <div className={styles.meta}>
                  <div>
                    <strong>Status:</strong>{" "}
                    {status === 1 ? "Active" : status === 3 ? "Swapped" : status}
                  </div>

                  <div>
                    <strong>Owner area:</strong> {normalized.OwnerArea || "—"}
                  </div>

                  <div>
                    <strong>Distance:</strong>{" "}
                    {distanceText ? (
                      <span className={`${styles.distanceInline} ${distanceClass}`}>
                        📍 {distanceText} away
                      </span>
                    ) : (
                      "—"
                    )}
                    {!viewerPostcode && (
                      <span className={styles.smallMuted}>
                        {" "}
                        • add your postcode to see miles
                      </span>
                    )}
                  </div>
                </div>

                {normalized.Notes && (
                  <div className={styles.notesBox}>
                    <div className={styles.notesTitle}>Notes</div>
                    <div className={styles.notes}>{normalized.Notes}</div>
                  </div>
                )}

                <div className={styles.divider} />

                {/* ✅ Hide owner action in public mode */}
                {!isPublic && (
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.dangerBtn}
                      onClick={markSwapped}
                      disabled={savingStatus || loading || status === 3}
                    >
                      {status === 3
                        ? "Already swapped"
                        : savingStatus
                        ? "Saving…"
                        : "Mark swapped"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BootDetail;